import { useCallback, useRef, type KeyboardEvent } from 'react';

import './Chips.css';

export type ChipOption = {
  value: string;
  label: string;
  count?: number;
  disabled?: boolean;
};

type CommonProps = {
  options: ChipOption[];
  ariaLabel: string;
  className?: string;
};

type SingleProps = CommonProps & {
  multi?: false;
  value: string | null;
  onChange: (value: string | null) => void;
};

type MultiProps = CommonProps & {
  multi: true;
  value: string[];
  onChange: (value: string[]) => void;
};

export type ChipsProps = SingleProps | MultiProps;

function isMulti(props: ChipsProps): props is MultiProps {
  return props.multi === true;
}

export function Chips(props: ChipsProps) {
  const { options, ariaLabel, className } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  const isSelected = useCallback(
    (v: string) => {
      if (isMulti(props)) return props.value.includes(v);
      return props.value === v;
    },
    [props],
  );

  const toggle = (opt: ChipOption) => {
    if (opt.disabled) return;
    if (isMulti(props)) {
      const next = props.value.includes(opt.value)
        ? props.value.filter((v) => v !== opt.value)
        : [...props.value, opt.value];
      props.onChange(next);
      return;
    }
    // Single mode: clicking the active chip clears the selection. This
    // matches the existing catalogue-chip behaviour where the user can
    // "deselect" by re-clicking and avoids a separate Reset control.
    props.onChange(props.value === opt.value ? null : opt.value);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (!containerRef.current?.contains(target)) return;
    const buttons = Array.from(
      containerRef.current.querySelectorAll<HTMLButtonElement>(
        'button[role="checkbox"], button[role="radio"]',
      ),
    ).filter((b) => !b.disabled);
    if (buttons.length === 0) return;
    const idx = buttons.indexOf(target as HTMLButtonElement);
    if (idx < 0) return;
    if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
      event.preventDefault();
      buttons[(idx + 1) % buttons.length].focus();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
      event.preventDefault();
      buttons[(idx - 1 + buttons.length) % buttons.length].focus();
    } else if (event.key === 'Home') {
      event.preventDefault();
      buttons[0].focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      buttons[buttons.length - 1].focus();
    }
  };

  const role = isMulti(props) ? 'group' : 'radiogroup';
  const buttonRole = isMulti(props) ? 'checkbox' : 'radio';

  return (
    <div
      ref={containerRef}
      role={role}
      aria-label={ariaLabel}
      className={['chips', className].filter(Boolean).join(' ')}
      onKeyDown={onKeyDown}
    >
      {options.map((opt) => {
        const selected = isSelected(opt.value);
        return (
          <button
            key={opt.value}
            type="button"
            role={buttonRole}
            aria-checked={selected}
            aria-disabled={opt.disabled || undefined}
            disabled={opt.disabled}
            onClick={() => toggle(opt)}
            className={[
              'chips__chip',
              selected ? 'chips__chip--selected' : null,
              opt.disabled ? 'chips__chip--disabled' : null,
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <span className="chips__label">{opt.label}</span>
            {typeof opt.count === 'number' ? (
              <span className="chips__count" aria-hidden="true">
                {opt.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
